import { Effect, Fiber, Layer } from 'effect';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useAppRuntime } from '../query/runtime-context';
import {
  type FollowAnchor,
  ReadAlongFollow,
  type ReadAlongFollowHandle,
  readAlongFollowLayer,
  type SyncReadAlongFollowAdapter,
  syncFollowAdapterLayer,
} from './follow-controller';
import type { FollowBand } from './follow-machine';

/**
 * The ONE React entry point for read-along follow, shared by DOM React and
 * RN. It owns exactly two effects — a lifecycle effect that forks the scoped
 * controller program on the app's ManagedRuntime (interrupting the fiber on
 * unmount closes the Scope: sampler, consumer, and settle fibers all die
 * together) and a one-line pref mirror — and zero logic: every scroll
 * decision lives in the pure follow machine.
 *
 * The adapter may be re-created every render (it usually closes over fresh
 * layout values); the controller always sees the latest one through a ref.
 */
export function useReadAlongFollow<A extends FollowAnchor>({
  adapter,
  prefEnabled,
  band,
  sampleIntervalMs,
}: {
  readonly adapter: SyncReadAlongFollowAdapter<A>;
  /** The user's auto-scroll preference (plus any view-level gating). */
  readonly prefEnabled: boolean;
  /** Fixed for the component's lifetime (a per-surface product constant). */
  readonly band: FollowBand;
  readonly sampleIntervalMs?: number;
}) {
  const runtime = useAppRuntime();
  const adapterRef = useRef(adapter);
  useLayoutEffect(() => {
    adapterRef.current = adapter;
  });
  const [pillVisible, setPillVisible] = useState(false);
  const handleRef = useRef<ReadAlongFollowHandle | null>(null);
  const initialRef = useRef({ band, prefEnabled, sampleIntervalMs });

  useEffect(() => {
    // Stable proxy so the layer (built once) always calls the latest
    // adapter, and pill visibility lands in React state exactly once.
    const proxy: SyncReadAlongFollowAdapter<A> = {
      getAnchor: () => adapterRef.current.getAnchor(),
      anchorsEqual: (a, b) => adapterRef.current.anchorsEqual(a, b),
      resolveTarget: (anchor) => adapterRef.current.resolveTarget(anchor),
      getViewport: () => adapterRef.current.getViewport(),
      isActiveVisible: (anchor, target) =>
        adapterRef.current.isActiveVisible(anchor, target),
      executeScroll: (target, options) =>
        adapterRef.current.executeScroll(target, options),
      cancelScroll: () => adapterRef.current.cancelScroll?.(),
      onPillChange: (visible) => {
        adapterRef.current.onPillChange?.(visible);
        setPillVisible(visible);
      },
    };
    const initial = initialRef.current;
    const program = Effect.gen(function* () {
      handleRef.current = yield* ReadAlongFollow;
      return yield* Effect.never;
    }).pipe(
      Effect.provide(
        readAlongFollowLayer({
          config: { band: initial.band },
          prefEnabled: initial.prefEnabled,
          ...(initial.sampleIntervalMs === undefined
            ? {}
            : { sampleIntervalMs: initial.sampleIntervalMs }),
        }).pipe(Layer.provide(syncFollowAdapterLayer(proxy))),
      ),
    );
    const fiber = runtime.runFork(program);
    return () => {
      handleRef.current = null;
      runtime.runFork(Fiber.interrupt(fiber));
    };
  }, [runtime]);

  useEffect(() => {
    const handle = handleRef.current;
    if (handle) runtime.runFork(handle.setPref(prefEnabled));
  }, [prefEnabled, runtime]);

  const methods = useMemo(() => {
    const run = (
      pick: (handle: ReadAlongFollowHandle) => Effect.Effect<void>,
    ) => {
      const handle = handleRef.current;
      if (handle) runtime.runFork(pick(handle));
    };
    return {
      /** Pill tap: re-engage and snap to the current anchor now. */
      engage: () => {
        run((h) => h.engage);
      },
      /** Word tap: re-engage; the tap's seek does the scrolling. */
      engageQuiet: () => {
        run((h) => h.engageQuiet);
      },
      /** Stop following without a gesture (outline/chapter navigation). */
      disengage: () => {
        run((h) => h.disengage);
      },
      /** Wire to USER gestures only — never to programmatic scrolls. */
      notifyUserScrollStart: () => {
        run((h) => h.notifyUserScrollStart);
      },
      notifyUserScrollEnd: () => {
        run((h) => h.notifyUserScrollEnd);
      },
      /** Resize, font change, zoom, virtualizer remount. */
      layoutInvalidated: () => {
        run((h) => h.layoutInvalidated);
      },
    };
  }, [runtime]);

  return { pillVisible, ...methods };
}

export type UseReadAlongFollowResult = ReturnType<typeof useReadAlongFollow>;
